import {RegisterOptions, useController, useFormContext} from "react-hook-form";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {DatePicker} from "@/components/datePicker/DatePicker.tsx";
import {DatePickerMode, DatePickerV3Props, DatePickerValue} from "@/components/datePicker/DatePicker.types.ts";
import {FormLabel} from "@/components/form/FormLabel.tsx";
import {FormSubtext} from "@/components/formSubtext/FormSubtext.tsx";
import {getFormError} from "@/lib/form/form.utils.ts";
import {FieldProps} from "@/pages/checks/components/form/fields/common.ts";

type DatePickerFieldProps<Mode extends DatePickerMode> = Omit<FieldProps, "label"> &
  Pick<DatePickerV3Props<Mode>, "onChange"> & {
    mode: Mode;
    datePickerV3Props?: Partial<Omit<DatePickerV3Props<Mode>, "mode" | keyof FieldProps>>;
    label?: string;
  };

export const DatePickerField = <Mode extends DatePickerMode>({
  name,
  label,
  tooltip,
  supportLink,
  helpText,
  required,
  onChange,
  mode = "single" as Mode,
  datePickerV3Props,
  ...reset
}: DatePickerFieldProps<Mode>) => {
  const {
    control,
    formState: {errors},
  } = useFormContext();
  const fieldError = getFormError(name, errors);
  const extraOptions: RegisterOptions = {
    ...(required && {required: "This field is required"}),
    ...(onChange && {onChange: onChange}),
  };
  const {field} = useController({control, name, rules: extraOptions});

  const {user} = useCurrentUserContext();
  const timezone = user!.account!.timezone;
  const dateFormat = user?.current_date_format;
  const timeFormat = user?.current_time_format;

  const datepickerProps = {
    mode,
    name: field.name,
    selected: field.value,
    onChange: (date: DatePickerValue<Mode>) => {
      // Clear to `null`, not `undefined`: react-hook-form treats `undefined` on a
      // controlled field as "unset" and falls back to the field's defaultValue, which
      // resurrects the original date in the picker while the submitted value stays
      // empty. `null` is a defined empty value, so the cleared state sticks.
      field.onChange(date ?? null);
    },
    dateFormat,
    timezone,
    timeFormat,
    hasPopoverInput: true,
    hasTimeSelect: true,
    ...reset,
    ...datePickerV3Props,
  };

  return (
    <div>
      {label && (
        <FormLabel required={required} forId={name} helpText={tooltip}>
          {label}
        </FormLabel>
      )}

      {/*I couldn't figure out how to type the props correctly with the complex union type of DatePicker*/}
      <DatePicker {...(datepickerProps as DatePickerV3Props<"single" | "range" | "split">)} />

      <FormSubtext subText={helpText} supportLink={supportLink} error={fieldError?.toString()} />
    </div>
  );
};
